import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { KeycloakService } from '../services/keycloak.service';

/**
 * Hängt an alle API-Requests ein frisches Bearer-Token aus dem uvi-Realm.
 * Auf 401 wird genau einmal ein erzwungener Refresh + Retry versucht;
 * schlägt auch der fehl, wird der Fehler durchgereicht (kein Redirect-Loop
 * bei Endpunkten, die das Portal-Token strukturell ablehnen).
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const keycloak = inject(KeycloakService);

  // Nur für API-Requests
  if (!req.url.startsWith(environment.apiUri)) {
    return next(req);
  }

  const withToken = (request: HttpRequest<unknown>, token: string): HttpRequest<unknown> =>
    request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });

  return from(keycloak.getValidToken()).pipe(
    switchMap((token) => {
      const authedReq = token ? withToken(req, token) : req;

      return next(authedReq).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status !== 401) {
            return throwError(() => error);
          }

          return from(keycloak.forceRefreshToken()).pipe(
            switchMap((newToken) => {
              if (!newToken || newToken === token) {
                return throwError(() => error);
              }
              return next(withToken(req, newToken));
            }),
          );
        }),
      );
    }),
  );
};
